import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getMyListings, type OwnerListing } from "@/services/ownerService";
import { isPremiumPlan } from "@/utils/plans";

/**
 * Small dashboard nudge for Premium owners who haven't added a video tour yet.
 * Renders nothing when there is no Premium listing missing a video, so paying
 * owners who already added one are never nagged.
 */
export default function VideoTourNudge() {
  const { accessToken } = useAuth();
  const [listings, setListings] = useState<OwnerListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getMyListings(accessToken)
      .then((res) => {
        if (active) setListings(res);
      })
      .catch(() => {
        /* the nudge is non-critical — stay silent on failure */
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);

  const missing = listings.filter((l) => isPremiumPlan(l.planSlug) && !l.videoUrl);

  if (loading || missing.length === 0) return null;

  const names = missing.map((l) => l.name);
  const subject = names.length === 1 ? names[0] : `${names.length} Premium listings`;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-accent-200 bg-accent-50/70 px-5 py-3.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
        <i className="ri-video-line text-base" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground-950">Add a video tour</p>
        <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">
          {subject} {missing.length === 1 ? "is" : "are"} on Premium but{" "}
          {missing.length === 1 ? "doesn't" : "don't"} have a video yet. Paste a YouTube or Vimeo
          link to show a Video Tour on the listing page.
        </p>
      </div>
      <Link
        to="/listings"
        className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
      >
        <i className="ri-add-line text-sm" /> Add video
      </Link>
    </div>
  );
}